import React, { useState, useMemo } from 'react';
import type { ExtendedCardData } from '../../utils/cardManager';

interface GroupCardSelectionModalProps {
  allCards: ExtendedCardData[];
  /** 現在許可されているカードIDのセット */
  allowedCardIds: Set<string>;
  /** グループ名（タイトル表示用） */
  groupName: string;
  /** グループカラー（テーマ色） */
  groupColor: string;
  onSave: (allowedIds: Set<string>) => void;
  onClose: () => void;
  isMobile?: boolean;
}

type FilterAttribute = '全て' | '炎' | '水' | '光' | '闇' | '原始' | '未来' | 'なし';
type FilterType = '全て' | 'monster' | 'magic' | 'field';

export const GroupCardSelectionModal: React.FC<GroupCardSelectionModalProps> = ({
  allCards,
  allowedCardIds,
  groupName,
  groupColor,
  onSave,
  onClose,
  isMobile = false,
}) => {
  // ローカルステート（保存前に編集可能）
  const [localAllowed, setLocalAllowed] = useState<Set<string>>(new Set(allowedCardIds));
  const [searchText, setSearchText] = useState('');
  const [filterAttribute, setFilterAttribute] = useState<FilterAttribute>('全て');
  const [filterType, setFilterType] = useState<FilterType>('全て');

  // フィルタリングされたカード一覧
  const filteredCards = useMemo(() => {
    return allCards.filter((card) => {
      // テキスト検索
      if (searchText && !card.name.includes(searchText) && !card.id.includes(searchText)) {
        return false;
      }
      // 属性フィルタ
      if (filterAttribute !== '全て' && card.attribute !== filterAttribute) {
        return false;
      }
      // タイプフィルタ
      if (filterType !== '全て' && card.type !== filterType) {
        return false;
      }
      return true;
    });
  }, [allCards, searchText, filterAttribute, filterType]);

  // カードの許可/禁止を切り替え
  const toggleAllowed = (cardId: string) => {
    setLocalAllowed((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(cardId)) {
        newSet.delete(cardId);
      } else {
        newSet.add(cardId);
      }
      return newSet;
    });
  };

  // 表示中のカードを全て許可
  const allowAllFiltered = () => {
    setLocalAllowed((prev) => {
      const newSet = new Set(prev);
      filteredCards.forEach((card) => newSet.add(card.id));
      return newSet;
    });
  };

  // 表示中のカードを全て禁止
  const disallowAllFiltered = () => {
    setLocalAllowed((prev) => {
      const newSet = new Set(prev);
      filteredCards.forEach((card) => newSet.delete(card.id));
      return newSet;
    });
  };

  // 全てのカードを許可
  const allowAll = () => {
    setLocalAllowed(new Set(allCards.map((c) => c.id)));
  };

  // 全てのカードを禁止
  const disallowAll = () => {
    setLocalAllowed(new Set());
  };

  // 保存
  const handleSave = () => {
    onSave(localAllowed);
    onClose();
  };

  // 属性の色
  const getAttributeColor = (attr: string): string => {
    switch (attr) {
      case '炎': return '#ff6b6b';
      case '水': return '#6b9eff';
      case '光': return '#ffd700';
      case '闇': return '#9d4ce6';
      case '原始': return '#6bff6b';
      case '未来': return '#00bcd4';
      default: return '#888';
    }
  };

  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'monster': return 'M';
      case 'magic': return '魔';
      case 'field': return 'F';
      default: return '?';
    }
  };

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div
        style={isMobile ? { ...styles.modal, ...styles.modalMobile } : styles.modal}
        onClick={(e) => e.stopPropagation()}
      >
        {/* ヘッダー */}
        <div style={{ ...styles.header, borderBottom: `2px solid ${groupColor}` }}>
          <h2 style={styles.title}>
            <span style={{ color: groupColor }}>■</span> {groupName} - カード設定
          </h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        {/* 説明 */}
        <p style={styles.description}>
          このグループで使用可能なカードを選択してください。
          <br />
          選択中: <strong style={{ color: groupColor }}>{localAllowed.size}</strong> / {allCards.length} 枚
        </p>

        {/* フィルター */}
        <div style={isMobile ? styles.filtersMobile : styles.filters}>
          <input
            type="text"
            placeholder="カード名/ID検索..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={styles.searchInput}
          />
          <select
            value={filterAttribute}
            onChange={(e) => setFilterAttribute(e.target.value as FilterAttribute)}
            style={styles.select}
          >
            <option value="全て">全属性</option>
            <option value="炎">炎</option>
            <option value="水">水</option>
            <option value="光">光</option>
            <option value="闇">闇</option>
            <option value="原始">原始</option>
            <option value="未来">未来</option>
            <option value="なし">なし</option>
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value as FilterType)}
            style={styles.select}
          >
            <option value="全て">全タイプ</option>
            <option value="monster">モンスター</option>
            <option value="magic">魔法</option>
            <option value="field">フィールド</option>
          </select>
        </div>

        {/* 一括操作ボタン */}
        <div style={styles.bulkActions}>
          <button onClick={allowAllFiltered} style={{ ...styles.bulkButton, background: '#4a6a4a' }}>
            表示中を全て許可
          </button>
          <button onClick={disallowAllFiltered} style={{ ...styles.bulkButton, background: '#6a4a4a' }}>
            表示中を全て禁止
          </button>
          <button onClick={allowAll} style={{ ...styles.bulkButton, background: '#3a5a3a' }}>
            全て許可
          </button>
          <button onClick={disallowAll} style={{ ...styles.bulkButton, background: '#5a3a3a' }}>
            全て禁止
          </button>
        </div>

        {/* カード一覧 */}
        <div style={styles.cardList}>
          {filteredCards.length === 0 ? (
            <p style={styles.noResults}>該当するカードがありません</p>
          ) : (
            filteredCards.map((card) => {
              const isAllowed = localAllowed.has(card.id);
              return (
                <div
                  key={card.id}
                  style={{
                    ...styles.cardItem,
                    ...(isAllowed ? styles.cardItemAllowed : styles.cardItemDisallowed),
                  }}
                  onClick={() => toggleAllowed(card.id)}
                >
                  <div style={styles.cardCheckbox}>
                    <input
                      type="checkbox"
                      checked={isAllowed}
                      onChange={() => toggleAllowed(card.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  <div
                    style={{
                      ...styles.cardAttribute,
                      background: getAttributeColor(card.attribute),
                    }}
                  >
                    {card.attribute?.charAt(0) || '?'}
                  </div>
                  <div style={styles.cardType}>{getTypeLabel(card.type)}</div>
                  <div style={styles.cardInfo}>
                    <span style={styles.cardName}>{card.name}</span>
                    <span style={styles.cardId}>{card.id}</span>
                  </div>
                  <div style={styles.cardCost}>
                    {card.cost}SP
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* フッター */}
        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelButton}>
            キャンセル
          </button>
          <button
            onClick={handleSave}
            disabled={localAllowed.size === 0}
            style={{
              ...styles.saveButton,
              background: localAllowed.size === 0 ? '#555' : groupColor,
              cursor: localAllowed.size === 0 ? 'not-allowed' : 'pointer',
            }}
          >
            保存 ({localAllowed.size}枚)
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2100,
  },
  modal: {
    background: '#1a1a2e',
    borderRadius: '12px',
    border: '2px solid #444',
    width: '90%',
    maxWidth: '700px',
    maxHeight: '85vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalMobile: {
    width: '95%',
    maxHeight: '90vh',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
  },
  title: {
    margin: 0,
    fontSize: '18px',
    color: '#fff',
  },
  closeButton: {
    background: 'transparent',
    border: 'none',
    color: '#888',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px 8px',
  },
  description: {
    margin: 0,
    padding: '12px 20px',
    color: '#aaa',
    fontSize: '14px',
    borderBottom: '1px solid #333',
  },
  filters: {
    display: 'flex',
    gap: '10px',
    padding: '12px 20px',
    borderBottom: '1px solid #333',
    flexWrap: 'wrap',
  },
  filtersMobile: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px 16px',
    borderBottom: '1px solid #333',
  },
  searchInput: {
    flex: 1,
    minWidth: '150px',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #444',
    background: '#2a2a3e',
    color: '#fff',
    fontSize: '14px',
  },
  select: {
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #444',
    background: '#2a2a3e',
    color: '#fff',
    fontSize: '14px',
    cursor: 'pointer',
  },
  bulkActions: {
    display: 'flex',
    gap: '8px',
    padding: '10px 20px',
    borderBottom: '1px solid #333',
    flexWrap: 'wrap',
  },
  bulkButton: {
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    background: '#4a4a6a',
    color: '#fff',
    fontSize: '12px',
    cursor: 'pointer',
  },
  cardList: {
    flex: 1,
    overflow: 'auto',
    padding: '10px 20px',
  },
  noResults: {
    color: '#888',
    textAlign: 'center',
    padding: '20px',
  },
  cardItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 12px',
    borderRadius: '6px',
    marginBottom: '6px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  cardItemAllowed: {
    background: '#2a3e2a',
    border: '1px solid #4a6a4a',
  },
  cardItemDisallowed: {
    background: '#2a2a3e',
    border: '1px solid #333',
    opacity: 0.6,
  },
  cardCheckbox: {
    display: 'flex',
    alignItems: 'center',
  },
  cardAttribute: {
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: '#fff',
  },
  cardType: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#aaa',
    background: '#3a3a5e',
  },
  cardInfo: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0,
  },
  cardName: {
    color: '#fff',
    fontSize: '14px',
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  cardId: {
    color: '#666',
    fontSize: '11px',
  },
  cardCost: {
    color: '#6b9eff',
    fontSize: '12px',
    fontWeight: 'bold',
    minWidth: '35px',
    textAlign: 'right',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    padding: '16px 20px',
    borderTop: '1px solid #444',
  },
  cancelButton: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: '1px solid #666',
    background: 'transparent',
    color: '#aaa',
    fontSize: '14px',
    cursor: 'pointer',
  },
  saveButton: {
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    color: '#fff',
    fontSize: '14px',
    fontWeight: 'bold',
  },
};
